<template>
  <div
    v-click-outside="fecharLista"
    class="mapa-busca"
    :class="{ 'mapa-busca--desabilitada': desabilitada }"
  >
    <div class="mapa-busca__campo">
      <md-icon class="mapa-busca__icone">search</md-icon>
      <input
        ref="input"
        v-model="termo"
        class="mapa-busca__input"
        type="search"
        :placeholder="placeholder"
        :disabled="desabilitada"
        autocomplete="off"
        @focus="listaAberta = true"
        @keydown.esc="fecharLista"
      />
    </div>

    <ul v-if="mostrarLista" class="mapa-busca__lista">
      <li v-if="buscando" class="mapa-busca__vazio">Buscando…</li>
      <li
        v-else-if="resultados.length === 0 && termoTrimido.length >= 2"
        class="mapa-busca__vazio"
      >
        Nenhum resultado
      </li>
      <li
        v-for="(item, index) in resultados"
        :key="chaveItem(item, index)"
        class="mapa-busca__item"
        @mousedown.prevent="escolher(item)"
      >
        <span class="mapa-busca__tipo">{{ rotuloTipo(item.tipo) }}</span>
        <span class="mapa-busca__titulo">{{ item.titulo }}</span>
        <span v-if="item.subtitulo" class="mapa-busca__sub">{{
          item.subtitulo
        }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { buscarNoMapa } from '@/utils/mapa-camadas'

const DEBOUNCE_MS = 300
const MIN_TERMOS = 2

export default {
  name: 'EquipamentoMapaBusca',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__mapaBuscaClick = (event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('mousedown', el.__mapaBuscaClick)
      },
      unbind(el) {
        document.removeEventListener('mousedown', el.__mapaBuscaClick)
      },
    },
  },
  props: {
    localId: {
      type: [String, Number],
      required: true,
    },
    desabilitada: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      termo: '',
      resultados: [],
      buscando: false,
      listaAberta: false,
      debounceTimer: null,
      buscaSeq: 0,
    }
  },
  computed: {
    termoTrimido() {
      return (this.termo || '').trim()
    },
    mostrarLista() {
      return (
        !this.desabilitada &&
        this.listaAberta &&
        this.termoTrimido.length >= MIN_TERMOS
      )
    },
    placeholder() {
      return this.desabilitada
        ? 'Carregando camadas…'
        : 'Buscar no mapa ("nome" = exato)'
    },
  },
  watch: {
    termo() {
      this.agendarBusca()
    },
    desabilitada(valor) {
      if (valor) {
        this.fecharLista()
        clearTimeout(this.debounceTimer)
        this.buscando = false
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onAtalhoBusca)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onAtalhoBusca)
    clearTimeout(this.debounceTimer)
  },
  methods: {
    onAtalhoBusca(e) {
      if ((e.key !== 'f' && e.key !== 'F') || !(e.ctrlKey || e.metaKey)) {
        return
      }
      if (this.desabilitada) {
        return
      }

      e.preventDefault()
      const input = this.$refs.input
      if (!input) {
        return
      }

      input.focus()
      input.select()
    },
    agendarBusca() {
      clearTimeout(this.debounceTimer)
      if (this.desabilitada) {
        return
      }
      const termo = this.termoTrimido
      if (termo.length < MIN_TERMOS) {
        this.resultados = []
        this.buscando = false
        return
      }

      this.listaAberta = true
      this.debounceTimer = setTimeout(() => this.buscar(termo), DEBOUNCE_MS)
    },
    async buscar(termo) {
      const seq = ++this.buscaSeq
      this.buscando = true
      const resposta = await buscarNoMapa(this.localId, termo)
      if (seq !== this.buscaSeq) {
        return
      }

      this.resultados = resposta?.resultados ?? []
      this.buscando = false
    },
    escolher(item) {
      this.listaAberta = false
      this.$emit('selecionar', item)
    },
    fecharLista() {
      this.listaAberta = false
    },
    chaveItem(item, index) {
      return [
        item.tipo,
        item.camadaId,
        item.sensorId,
        item.equipamentoId,
        index,
      ]
        .filter((v) => v != null && v !== '')
        .join('-')
    },
    rotuloTipo(tipo) {
      if (tipo === 'equipamento') return 'Equipamento'
      if (tipo === 'sensor') return 'Sensor'
      if (tipo === 'inp') return 'INP'
      if (tipo === 'kml') return 'KML'
      return tipo
    },
  },
}
</script>

<style lang="scss" scoped>
.mapa-busca {
  position: absolute;
  top: 12px;
  // À esquerda do zoom do Mapbox (10px margem + 29px botão + folga).
  right: 56px;
  z-index: 6;
  width: 280px;
  max-width: calc(100% - 68px);
}

.mapa-busca__campo {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  padding: 0 10px;
  height: 44px;
}

.mapa-busca--desabilitada .mapa-busca__campo {
  opacity: 0.6;
  cursor: not-allowed;
}

.mapa-busca__icone {
  color: #666;
  font-size: 20px !important;
}

.mapa-busca__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #333;
  min-width: 0;

  &::placeholder {
    color: #999;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.mapa-busca__lista {
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  max-height: 320px;
  overflow-y: auto;
}

.mapa-busca__item {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
}

.mapa-busca__tipo {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #800080;
}

.mapa-busca__titulo {
  font-size: 13px;
  color: #222;
}

.mapa-busca__sub {
  font-size: 11px;
  color: #777;
}

.mapa-busca__vazio {
  padding: 10px 12px;
  font-size: 13px;
  color: #777;
}
</style>
